import type {
    SiteBuilderBlock,
    SiteBuilderDesign as SiteTemplateDesign,
} from "./siteBuilderTypes";
import {
    SITE_BUILDER_INNER_BLOCK_TYPES,
} from "./innerContentBlocks";
import {
    BOOKING_PORTAL_BLOCK_TYPE,
    BOOKING_PORTAL_VARIANTS,
    BOOKING_SYSTEM_PAGE_KEY,
} from "./bookingPortal";
import { BOOKING_PORTAL_PROFILES } from "./bookingPortalProfiles";
import {
    WEB_FOOTER_EXTENDED_SLOT_KEYS,
    WEB_FOOTER_LAYOUT_VARIANTS,
} from "./webFooterLayout";
import {
    DEFAULT_SITE_NAVIGATION_SETTINGS,
    SITE_NAVIGATION_ALIGNMENT_OPTION,
    SITE_NAVIGATION_BLOCK_TYPE,
    SITE_NAVIGATION_INSPECTOR_FIELDS,
    SITE_NAVIGATION_MENU_LOCATION_OPTION,
    SITE_NAVIGATION_MOBILE_MENU_LOCATION_OPTION,
    SITE_NAVIGATION_OPEN_MODE_OPTION,
    SITE_NAVIGATION_RUNTIME_ROLE,
    SITE_NAVIGATION_RUNTIME_ROLE_OPTION,
    SITE_NAVIGATION_SOURCE_OPTION,
    SITE_NAVIGATION_THEME_OPTION,
    SITE_NAVIGATION_VARIANT_OPTION,
} from "./siteNavigation";

type SiteTemplateBlock = SiteBuilderBlock;

export const SITE_BUILDER_SCHEMA_VERSION = 37;

export const SITE_BUILDER_STYLE_SNAPSHOT_OPTION = "builderStyleSnapshot";
export const SITE_BUILDER_ZONE_OPTION = "builderZone";
export const SITE_BUILDER_LAYOUT_CONTAINER_OPTION = "builderLayoutContainer";
export const SITE_BUILDER_EXTENDED_SLOT_OPTION = "builderExtendedSlot";

export type SiteBuilderZoneKind = "document" | "textFlow" | "slot" | "extendedSlot" | "grid" | "fixed";
export type SiteBuilderDropLine = "before" | "after" | "below" | "left" | "right" | "inside";
export type SiteBuilderBlockCategory =
    | "text"
    | "layout"
    | "media"
    | "commerce"
    | "data"
    | "social"
    | "utility";
export type SiteBuilderInspectorFieldKind = "text" | "url" | "color" | "number" | "select" | "toggle" | "media" | "richText" | "dateTime";
export type SiteBuilderWarningCode = "missingContent" | "missingLink" | "missingMedia" | "overflow" | "incompleteValue";
export type SiteBuilderLocalizationFormat = "plainText" | "richText";

export type SiteBuilderLocalizationFieldDescriptor = Readonly<{
    fieldCode: string;
    format: SiteBuilderLocalizationFormat;
    localizable: boolean;
    multiline: boolean;
}>;

export type SiteBuilderInspectorField = {
    key: string;
    kind: SiteBuilderInspectorFieldKind;
    labelKey?: string;
    options?: string[];
    scope?: "content" | "options" | "style";
};

export type SiteBuilderWarningDefinition = {
    code: SiteBuilderWarningCode;
    severity: "info" | "warning" | "error";
    field?: string;
};

export type SiteBuilderResolvedWarning = SiteBuilderWarningDefinition & {
    blockType: string;
};

export type SiteBuilderResponsiveRules = {
    stackOnMobile?: boolean;
    mobile?: Record<string, unknown>;
    tablet?: Record<string, unknown>;
    desktop?: Record<string, unknown>;
};

export type SiteBuilderRendererDefinition = {
    component: string;
    publicComponent?: string;
};

export type SiteBuilderZoneDefinition = {
    key: string;
    kind: SiteBuilderZoneKind;
    accepts?: string[];
    capacity?: number;
    dropLines?: SiteBuilderDropLine[];
    textFlow?: boolean;
    extendedRows?: boolean;
};

export type SiteBuilderBlockDefinition = {
    type: string;
    category: SiteBuilderBlockCategory;
    insertable: boolean;
    textFlow: boolean;
    standalone: boolean;
    layoutContainer: boolean;
    dropLines: SiteBuilderDropLine[];
    zones?: SiteBuilderZoneDefinition[];
    contextualDefaults?: {
        content?: Record<string, unknown>;
        options?: Record<string, unknown>;
        slots?: Record<string, SiteTemplateBlock[]>;
    };
    inspector: SiteBuilderInspectorField[];
    localization: readonly SiteBuilderLocalizationFieldDescriptor[];
    renderer: SiteBuilderRendererDefinition;
    responsive: SiteBuilderResponsiveRules;
    styleCapabilities: string[];
    warnings: SiteBuilderWarningDefinition[];
    allowedPageTypes?: string[];
    systemOnly?: boolean;
};

export type SiteBuilderStyleSnapshot = {
    colors?: SiteTemplateDesign["colors"];
    typography?: SiteTemplateDesign["typography"];
    layout?: SiteTemplateDesign["layout"];
    components?: SiteTemplateDesign["components"];
    context?: {
        presetKey?: string;
        zoneKey?: string;
        slotSize?: string;
    };
};

const textDropLines: SiteBuilderDropLine[] = ["below"];
const inlineControlDropLines: SiteBuilderDropLine[] = ["left", "right", "below"];
const blockDropLines: SiteBuilderDropLine[] = ["before", "after", "below"];
const layoutDropLines: SiteBuilderDropLine[] = ["before", "after", "below", "inside"];
const allDropLines: SiteBuilderDropLine[] = ["before", "after", "below", "left", "right", "inside"];
const richContentAcceptedTypes = [
    "column",
    BOOKING_PORTAL_BLOCK_TYPE,
    SITE_NAVIGATION_BLOCK_TYPE,
    ...SITE_BUILDER_INNER_BLOCK_TYPES,
    "paragraph",
    "heading",
    "quote",
    "list",
    "checklist",
    "table",
    "link",
    "emoji",
    "signature",
    "media",
    "uploadFile",
];
const textHighlightWidthOptions = ["100%", "90%", "80%", "75%", "66%", "50%", "33%"];
const blockquoteWidthOptions = ["100%", "90%", "80%", "75%", "66%", "50%"];
const blockquoteShapeOptions = ["none", "slant", "arch", "wave", "jagged", "rounded"];
const blockquoteDecorationOptions = ["none", "leftLine", "icon"];
const blockquoteAuthorAlignmentOptions = ["left", "center", "right"];
const formActionOptions = ["sendEmail"];
const formMarkedFieldOptions = ["required", "optional", "none"];
const formLabelsPositionOptions = ["top", "sideGap", "sideNoGap", "none"];
const formOnSuccessOptions = ["redirect", "nothing", "message"];
// Keep authored field choices aligned with the persisted lead-form schema.
// Legacy documents with other values still render through the backend's safe
// text fallback, but the editor must not create unsupported field semantics.
const formFieldTypeOptions = [
    "text",
    "longText",
    "email",
    "telephone",
    "selection",
];
const formFieldRequirementOptions = [
    "required",
    "none",
];
const submitButtonPositionOptions = ["left", "center", "right", "inputAligned"];
const animationOptions = ["", "fade-in", "fade-up", "fade-down", "fade-left", "fade-right", "zoom-in", "zoom-out", "flip-x", "flip-y", "rotate-in", "blur-in", "bounce-in", "float-in", "clip-up", "reveal-left", "reveal-right"];
const badgeStyleOptions = ["primary", "secondary", "success", "info", "warning", "danger", "light", "dark"];
const borderStyleOptions = ["solid", "dashed", "dotted", "double"];
const textFlowDropLineTypes = new Set([
    "checklist",
    "code",
    "heading",
    "list",
    "paragraph",
    "quote",
    "text",
]);
const actionDropLineTypes = new Set([
    "addToCartButton",
    "appointmentButton",
    "badge",
    "button",
    "clientPortalButton",
    "ctaBadge",
    "donationButton",
    "facebook",
    "instagram",
    "link",
    "socialLink",
    "supportedPaymentButton",
]);

const textInspector: SiteBuilderInspectorField[] = [
    { key: "text", kind: "richText", scope: "content" },
    { key: "textColor", kind: "color", scope: "style" },
    { key: "fontFamily", kind: "select", scope: "style" },
];

const headingInspector: SiteBuilderInspectorField[] = [
    { key: "title", kind: "richText", scope: "content" },
    { key: "level", kind: "select", options: ["1", "2", "3"], scope: "options" },
    { key: "textColor", kind: "color", scope: "style" },
];

const textHighlightInspector: SiteBuilderInspectorField[] = [
    { key: "alignment", kind: "select", options: ["left", "center", "right"], scope: "options" },
    { key: "backgroundColor", kind: "color", scope: "style" },
    { key: "textHighlightWidth", kind: "select", options: textHighlightWidthOptions, scope: "options" },
];

const blockquoteInspector: SiteBuilderInspectorField[] = [
    { key: "backgroundColor", kind: "color", scope: "options" },
    { key: "blockquoteShape", kind: "select", options: blockquoteShapeOptions, scope: "options" },
    { key: "blockquoteWidth", kind: "select", options: blockquoteWidthOptions, scope: "options" },
    { key: "edgeSpacing", kind: "number", scope: "options" },
    { key: "blockquoteDecoration", kind: "select", options: blockquoteDecorationOptions, scope: "options" },
    { key: "showAuthor", kind: "toggle", scope: "options" },
    { key: "authorAlignment", kind: "select", options: blockquoteAuthorAlignmentOptions, scope: "options" },
    { key: "borderWidth", kind: "number", scope: "options" },
    { key: "borderRadius", kind: "number", scope: "options" },
    { key: "animation", kind: "select", options: animationOptions, scope: "options" },
];

const formInspector: SiteBuilderInspectorField[] = [
    { key: "formName", kind: "text", scope: "content" },
    { key: "formAction", kind: "select", options: formActionOptions, scope: "options" },
    { key: "formMarkedFields", kind: "select", options: formMarkedFieldOptions, scope: "options" },
    { key: "formMarker", kind: "text", scope: "options" },
    { key: "formLabelsPosition", kind: "select", options: formLabelsPositionOptions, scope: "options" },
    { key: "formLabelsWidth", kind: "text", scope: "options" },
    { key: "formOnSuccess", kind: "select", options: formOnSuccessOptions, scope: "options" },
    { key: "successUrl", kind: "url", scope: "content" },
];

const fieldInspector: SiteBuilderInspectorField[] = [
    { key: "label", kind: "text", scope: "content" },
    { key: "fieldInputType", kind: "select", options: formFieldTypeOptions, scope: "options" },
    { key: "rows", kind: "number", scope: "options" },
    { key: "choices", kind: "text", scope: "content" },
    { key: "description", kind: "text", scope: "content" },
    { key: "showDescription", kind: "toggle", scope: "options" },
    { key: "placeholder", kind: "text", scope: "content" },
    { key: "defaultValue", kind: "text", scope: "content" },
    { key: "requirement", kind: "select", options: formFieldRequirementOptions, scope: "options" },
];

const submitButtonInspector: SiteBuilderInspectorField[] = [
    { key: "buttonPosition", kind: "select", options: submitButtonPositionOptions, scope: "options" },
];

const buttonInspector: SiteBuilderInspectorField[] = [
    { key: "label", kind: "text", scope: "content" },
    { key: "href", kind: "url", scope: "content" },
    { key: "buttonPreset", kind: "select", options: ["primary", "secondary", "custom"], scope: "options" },
    { key: "size", kind: "select", options: ["small", "medium", "large"], scope: "options" },
    { key: "alignment", kind: "select", options: ["left", "center", "right"], scope: "options" },
    { key: "backgroundColor", kind: "color", scope: "style" },
    { key: "textColor", kind: "color", scope: "style" },
];

const textLinkInspector = buttonInspector.filter(
    ({ key }) => key !== "buttonPreset" && key !== "size" && key !== "alignment",
);

const socialLinkInspector = buttonInspector.filter(
    ({ key }) => key !== "size" && key !== "alignment",
);

const mediaInspector: SiteBuilderInspectorField[] = [
    { key: "image", kind: "media", scope: "content" },
    { key: "alt", kind: "text", scope: "content" },
    { key: "objectFit", kind: "select", options: ["cover", "contain"], scope: "options" },
    {
        key: "objectPosition",
        kind: "select",
        options: [
            "center",
            "top",
            "right",
            "bottom",
            "left",
            "top left",
            "top right",
            "bottom left",
            "bottom right",
        ],
        scope: "options",
    },
];

const layoutInspector: SiteBuilderInspectorField[] = [
    { key: "gap", kind: "number", scope: "options" },
    { key: "stackOnMobile", kind: "toggle", scope: "options" },
    { key: "alignment", kind: "select", options: ["left", "center", "right"], scope: "options" },
];

const missingContentWarning: SiteBuilderWarningDefinition = { code: "missingContent", severity: "warning" };
const missingLinkWarning: SiteBuilderWarningDefinition = { code: "missingLink", severity: "warning", field: "href" };
const missingMediaWarning: SiteBuilderWarningDefinition = { code: "missingMedia", severity: "warning" };
const commonVisualInspector: SiteBuilderInspectorField[] = [
    { key: "opacity", kind: "number", scope: "style" },
    { key: "zIndex", kind: "number", scope: "style" },
];

const singleLineRichText = (
    fieldCode: string,
): SiteBuilderLocalizationFieldDescriptor => ({
    fieldCode,
    format: "richText",
    localizable: true,
    multiline: false,
});

const multilineRichText = (
    fieldCode: string,
): SiteBuilderLocalizationFieldDescriptor => ({
    fieldCode,
    format: "richText",
    localizable: true,
    multiline: true,
});

const plainText = (
    fieldCode: string,
): SiteBuilderLocalizationFieldDescriptor => ({
    fieldCode,
    format: "plainText",
    localizable: false,
    multiline: false,
});

const localizablePlainText = (
    fieldCode: string,
    multiline = false,
): SiteBuilderLocalizationFieldDescriptor => ({
    fieldCode,
    format: "plainText",
    localizable: true,
    multiline,
});

/**
 * Structural text-field metadata used by the neutral document codec.
 * Shop message keys and translation ownership stay in the Shop adapter.
 */
const siteBuilderLocalizationFields = new Map<string, readonly SiteBuilderLocalizationFieldDescriptor[]>([
    ["text", [multilineRichText("content.text")]],
    ["paragraph", [multilineRichText("content.text")]],
    ["heading", [
        singleLineRichText("content.title"),
        singleLineRichText("content.text"),
        singleLineRichText("content.eyebrow"),
    ]],
    ["quote", [
        multilineRichText("content.text"),
        singleLineRichText("content.author"),
    ]],
    ["code", [
        multilineRichText("content.code"),
        plainText("content.language"),
    ]],
    ["list", [
        multilineRichText("content.items"),
        plainText("content.style"),
    ]],
    ["checklist", [multilineRichText("content.items")]],
    ["link", [
        singleLineRichText("content.label"),
        plainText("content.href"),
    ]],
    ["button", [
        singleLineRichText("content.label"),
        plainText("content.href"),
    ]],
    ["clientPortalButton", [
        plainText("content.label"),
        plainText("content.icon"),
    ]],
    ["image", [localizablePlainText("content.alt")]],
    ["media", [localizablePlainText("content.alt")]],
    ["card", [
        singleLineRichText("content.title"),
        multilineRichText("content.text"),
        plainText("content.actionLabel"),
        plainText("content.href"),
    ]],
    ["accordionItem", [
        singleLineRichText("content.title"),
        multilineRichText("content.text"),
    ]],
    ["donationButton", [
        singleLineRichText("content.label"),
        plainText("content.href"),
        plainText("content.icon"),
    ]],
    ["addToCartButton", [
        singleLineRichText("content.label"),
        plainText("content.productUuid"),
        plainText("content.icon"),
    ]],
    ["supportedPaymentButton", [
        singleLineRichText("content.title"),
        multilineRichText("content.items"),
    ]],
    ["map", [
        singleLineRichText("content.title"),
        multilineRichText("content.address"),
        plainText("content.mapUrl"),
        plainText("content.latitude"),
        plainText("content.longitude"),
    ]],
    ["avatars", [
        singleLineRichText("content.title"),
        multilineRichText("content.items"),
    ]],
    ["table", [multilineRichText("content.items")]],
    ["icon", [
        localizablePlainText("content.label"),
        plainText("content.icon"),
        plainText("content.href"),
    ]],
    ["emoji", [localizablePlainText("content.text")]],
    ["signature", [multilineRichText("content.text")]],
    ["alert", [multilineRichText("content.text")]],
    ["form", [
        singleLineRichText("content.title"),
        multilineRichText("content.text"),
        localizablePlainText("content.submitLabel"),
        localizablePlainText("content.successMessage", true),
    ]],
    ["share", [singleLineRichText("content.title")]],
    ["socialMedia", [singleLineRichText("content.title")]],
    ["socialLink", [
        singleLineRichText("content.label"),
        plainText("content.href"),
    ]],
    ["textHighlight", [
        singleLineRichText("content.title"),
        multilineRichText("content.text"),
    ]],
    ["chart", [singleLineRichText("content.title")]],
    ["progressBar", [singleLineRichText("content.title")]],
    ["badge", [singleLineRichText("content.label")]],
    ["ctaBadge", [
        singleLineRichText("content.title"),
        singleLineRichText("content.label"),
        plainText("content.href"),
    ]],
    ["field", [
        localizablePlainText("content.label"),
        localizablePlainText("content.description"),
        localizablePlainText("content.placeholder"),
        localizablePlainText("content.defaultValue", true),
        localizablePlainText("content.choices", true),
    ]],
    ["submitButton", [singleLineRichText("content.label")]],
    ["blockquote", [
        multilineRichText("content.text"),
        singleLineRichText("content.author"),
    ]],
    ["searchBar", [
        localizablePlainText("content.placeholder"),
        localizablePlainText("content.buttonLabel"),
    ]],
]);

export function getSiteBuilderLocalizationFields(
    blockType: string,
): readonly SiteBuilderLocalizationFieldDescriptor[] {
    return siteBuilderLocalizationFields.get(blockType) ?? [];
}

function blockDefinition(
    definition: Omit<SiteBuilderBlockDefinition, "inspector" | "localization" | "renderer" | "responsive" | "warnings"> & Partial<Pick<SiteBuilderBlockDefinition, "inspector" | "localization" | "renderer" | "responsive" | "warnings">>,
): SiteBuilderBlockDefinition {
    const inspector = definition.inspector ?? [];

    return {
        ...definition,
        inspector: [
            ...inspector,
            ...commonVisualInspector.filter(({ key }) => !inspector.some((field) => field.key === key && field.scope === "style")),
        ],
        localization: definition.localization ?? getSiteBuilderLocalizationFields(definition.type),
        renderer: definition.renderer ?? { component: definition.type },
        responsive: definition.responsive ?? {},
        warnings: definition.warnings ?? [],
    };
}

const coreBlockDefinitions: SiteBuilderBlockDefinition[] = [
    blockDefinition({
        type: "text",
        category: "text",
        insertable: true,
        textFlow: true,
        standalone: true,
        layoutContainer: false,
        dropLines: textDropLines,
        contextualDefaults: { content: { text: "" } },
        inspector: textInspector,
        styleCapabilities: ["typography", "color", "surface", "size", "spacing"],
        warnings: [missingContentWarning],
    }),
    blockDefinition({
        type: "paragraph",
        category: "text",
        insertable: true,
        textFlow: true,
        standalone: true,
        layoutContainer: false,
        dropLines: textDropLines,
        contextualDefaults: { content: { text: "" } },
        inspector: textInspector,
        styleCapabilities: ["typography", "color", "spacing"],
        warnings: [missingContentWarning],
    }),
    blockDefinition({
        type: "heading",
        category: "text",
        insertable: true,
        textFlow: true,
        standalone: true,
        layoutContainer: false,
        dropLines: textDropLines,
        contextualDefaults: { content: { title: "" }, options: { level: 2 } },
        inspector: headingInspector,
        styleCapabilities: ["typography", "color", "spacing"],
        warnings: [missingContentWarning],
    }),
    blockDefinition({
        type: "quote",
        category: "text",
        insertable: true,
        textFlow: true,
        standalone: true,
        layoutContainer: false,
        dropLines: textDropLines,
        contextualDefaults: { content: { text: "" } },
        inspector: textInspector,
        styleCapabilities: ["typography", "color", "border", "spacing"],
        warnings: [missingContentWarning],
    }),
    blockDefinition({
        type: "code",
        category: "text",
        insertable: true,
        textFlow: true,
        standalone: true,
        layoutContainer: false,
        dropLines: textDropLines,
        contextualDefaults: {
            content: { code: "" },
            options: { embedAspectRatio: "wide" },
        },
        inspector: [
            { key: "code", kind: "richText", scope: "content" },
            { key: "embedAspectRatio", kind: "select", options: ["wide", "standard", "square"], scope: "options" },
        ],
        styleCapabilities: ["typography", "color", "surface", "spacing"],
        warnings: [missingContentWarning],
    }),
    blockDefinition({
        type: "list",
        category: "text",
        insertable: true,
        textFlow: true,
        standalone: true,
        layoutContainer: false,
        dropLines: textDropLines,
        contextualDefaults: { content: { items: "" } },
        inspector: [{ key: "items", kind: "richText", scope: "content" }],
        styleCapabilities: ["typography", "color", "spacing"],
        warnings: [missingContentWarning],
    }),
    blockDefinition({
        type: "checklist",
        category: "text",
        insertable: true,
        textFlow: true,
        standalone: true,
        layoutContainer: false,
        dropLines: textDropLines,
        contextualDefaults: { content: { items: "" } },
        inspector: [{ key: "items", kind: "richText", scope: "content" }],
        renderer: { component: "list", publicComponent: "list" },
        styleCapabilities: ["typography", "color", "spacing"],
        warnings: [missingContentWarning],
    }),
    blockDefinition({
        type: "button",
        category: "utility",
        insertable: true,
        textFlow: false,
        standalone: true,
        layoutContainer: false,
        dropLines: inlineControlDropLines,
        contextualDefaults: { content: { label: "", href: "#" }, options: { buttonPreset: "primary" } },
        inspector: buttonInspector,
        styleCapabilities: ["button", "color", "spacing", "radius"],
        warnings: [missingContentWarning, missingLinkWarning],
    }),
    blockDefinition({
        type: "clientPortalButton",
        category: "utility",
        insertable: false,
        textFlow: false,
        standalone: true,
        layoutContainer: false,
        dropLines: inlineControlDropLines,
        contextualDefaults: {
            content: { icon: "lucide:user", label: "Klientská zóna" },
            options: { buttonPreset: "primary" },
        },
        inspector: [
            { key: "label", kind: "richText", scope: "content" },
            { key: "icon", kind: "text", scope: "content" },
            ...buttonInspector.slice(2),
        ],
        styleCapabilities: ["button", "color", "spacing", "radius"],
        warnings: [missingContentWarning],
    }),
    blockDefinition({
        type: "link",
        category: "utility",
        insertable: true,
        textFlow: true,
        standalone: true,
        layoutContainer: false,
        dropLines: inlineControlDropLines,
        contextualDefaults: { content: { label: "", href: "#" } },
        inspector: textLinkInspector,
        styleCapabilities: ["typography", "color", "spacing"],
        warnings: [missingContentWarning, missingLinkWarning],
    }),
    blockDefinition({
        type: "image",
        category: "media",
        insertable: true,
        textFlow: false,
        standalone: true,
        layoutContainer: false,
        dropLines: blockDropLines,
        contextualDefaults: {
            content: { alt: "" },
            options: { objectFit: "cover" },
        },
        inspector: mediaInspector,
        styleCapabilities: ["media", "size", "radius", "spacing"],
        warnings: [missingMediaWarning],
    }),
    blockDefinition({
        type: "media",
        category: "media",
        insertable: true,
        textFlow: false,
        standalone: true,
        layoutContainer: false,
        dropLines: blockDropLines,
        contextualDefaults: {
            content: { alt: "" },
            options: { objectFit: "cover" },
        },
        inspector: mediaInspector,
        styleCapabilities: ["media", "size", "radius", "spacing"],
        warnings: [missingMediaWarning],
    }),
    blockDefinition({
        type: "richContent",
        category: "layout",
        insertable: true,
        textFlow: false,
        standalone: true,
        layoutContainer: true,
        dropLines: layoutDropLines,
        zones: [
            {
                key: "grid",
                kind: "grid",
                accepts: richContentAcceptedTypes,
                capacity: 256,
                extendedRows: true,
                dropLines: allDropLines,
            },
            {
                key: "content",
                kind: "textFlow",
                accepts: richContentAcceptedTypes,
                capacity: 256,
                textFlow: true,
                extendedRows: true,
                dropLines: allDropLines,
            },
        ],
        contextualDefaults: {
            options: { stackOnMobile: true },
        },
        inspector: layoutInspector,
        responsive: { stackOnMobile: true },
        styleCapabilities: ["layout", "gap", "responsive", "spacing", "surface", "border", "radius"],
    }),
    blockDefinition({
        type: BOOKING_PORTAL_BLOCK_TYPE,
        category: "data",
        insertable: false,
        textFlow: false,
        standalone: true,
        layoutContainer: false,
        dropLines: blockDropLines,
        contextualDefaults: {
            options: {
                allowAnyResource: true,
                bookingProfile: "appointment",
                bookingVariant: "guided",
                daysVisible: "days7",
                showPrices: true,
                showServiceDescriptions: true,
            },
        },
        inspector: [
            { key: "bookingProfile", kind: "select", options: [...BOOKING_PORTAL_PROFILES], scope: "options" },
            { key: "bookingVariant", kind: "select", options: [...BOOKING_PORTAL_VARIANTS], scope: "options" },
            { key: "showPrices", kind: "toggle", scope: "options" },
            { key: "showServiceDescriptions", kind: "toggle", scope: "options" },
            { key: "allowAnyResource", kind: "toggle", scope: "options" },
        ],
        renderer: { component: BOOKING_PORTAL_BLOCK_TYPE, publicComponent: BOOKING_PORTAL_BLOCK_TYPE },
        styleCapabilities: ["surface", "border", "radius", "spacing", "size"],
        allowedPageTypes: [BOOKING_SYSTEM_PAGE_KEY],
        systemOnly: true,
    }),
    blockDefinition({
        type: "footer",
        category: "layout",
        insertable: false,
        textFlow: false,
        standalone: true,
        layoutContainer: true,
        dropLines: layoutDropLines,
        zones: [
            {
                key: "grid",
                kind: "grid",
                accepts: richContentAcceptedTypes,
                capacity: 256,
                extendedRows: true,
                dropLines: allDropLines,
            },
            ...WEB_FOOTER_EXTENDED_SLOT_KEYS.map((key) => ({
                key,
                kind: "grid" as const,
                accepts: richContentAcceptedTypes,
                capacity: 256,
                extendedRows: true,
                dropLines: allDropLines,
            })),
        ],
        contextualDefaults: {
            options: {
                footerVariant: "columns",
                gridColumns: 12,
                gridRowHeight: 48,
                stackOnMobile: true,
            },
        },
        inspector: [
            {
                key: "footerVariant",
                kind: "select",
                options: [...WEB_FOOTER_LAYOUT_VARIANTS],
                scope: "options",
            },
            ...layoutInspector,
        ],
        responsive: { stackOnMobile: true },
        renderer: { component: "footer", publicComponent: "footer" },
        styleCapabilities: ["layout", "gap", "responsive", "spacing", "surface", "border", "radius"],
        systemOnly: true,
    }),
    blockDefinition({
        type: "card",
        category: "layout",
        insertable: true,
        textFlow: false,
        standalone: true,
        layoutContainer: false,
        dropLines: blockDropLines,
        zones: [{ key: "content", kind: "textFlow", textFlow: true, dropLines: allDropLines }],
        contextualDefaults: {
            content: {
                actionLabel: "",
                href: "",
                title: "Obsahová karta",
                text: "Krátký popis, který vysvětlí hlavní hodnotu této karty.",
            },
            options: {
                backgroundColor: "var(--site-surface)",
                borderColor: "var(--site-border)",
                borderRadius: "var(--site-radius)",
                borderWidth: "1px",
                padding: "24px",
            },
        },
        inspector: [
            { key: "title", kind: "text", scope: "content" },
            { key: "text", kind: "richText", scope: "content" },
            { key: "actionLabel", kind: "text", scope: "content" },
            { key: "href", kind: "text", scope: "content" },
        ],
        styleCapabilities: ["typography", "layout", "surface", "border", "radius", "spacing"],
        warnings: [missingContentWarning],
    }),
    blockDefinition({
        type: "column",
        category: "layout",
        insertable: true,
        textFlow: false,
        standalone: false,
        layoutContainer: true,
        dropLines: layoutDropLines,
        zones: [{ key: "content", kind: "textFlow", textFlow: true, extendedRows: true, dropLines: allDropLines }],
        contextualDefaults: {
            options: {
                contentLayout: "vertical",
                stackOnMobile: true,
            },
        },
        inspector: [
            ...layoutInspector,
            { key: "contentLayout", kind: "select", options: ["vertical", "horizontal", "inline"], scope: "options" },
        ],
        responsive: { stackOnMobile: true },
        styleCapabilities: ["layout", "gap", "responsive", "spacing", "surface", "border", "radius"],
    }),
    blockDefinition({
        type: SITE_NAVIGATION_BLOCK_TYPE,
        category: "layout",
        insertable: false,
        textFlow: false,
        standalone: false,
        layoutContainer: true,
        dropLines: layoutDropLines,
        zones: [{
            key: "content",
            kind: "textFlow",
            accepts: ["link", "text", "button"],
            capacity: 64,
            textFlow: true,
            extendedRows: true,
            dropLines: allDropLines,
        }],
        contextualDefaults: {
            options: {
                [SITE_NAVIGATION_RUNTIME_ROLE_OPTION]: SITE_NAVIGATION_RUNTIME_ROLE,
                [SITE_NAVIGATION_VARIANT_OPTION]: DEFAULT_SITE_NAVIGATION_SETTINGS.variant,
                [SITE_NAVIGATION_SOURCE_OPTION]: DEFAULT_SITE_NAVIGATION_SETTINGS.source,
                [SITE_NAVIGATION_THEME_OPTION]: DEFAULT_SITE_NAVIGATION_SETTINGS.theme,
                [SITE_NAVIGATION_ALIGNMENT_OPTION]: DEFAULT_SITE_NAVIGATION_SETTINGS.alignment,
                [SITE_NAVIGATION_OPEN_MODE_OPTION]: DEFAULT_SITE_NAVIGATION_SETTINGS.openMode,
                [SITE_NAVIGATION_MENU_LOCATION_OPTION]: DEFAULT_SITE_NAVIGATION_SETTINGS.menuLocation,
                [SITE_NAVIGATION_MOBILE_MENU_LOCATION_OPTION]: DEFAULT_SITE_NAVIGATION_SETTINGS.mobileMenuLocation,
            },
            slots: { content: [] },
        },
        inspector: SITE_NAVIGATION_INSPECTOR_FIELDS.map(({
            options,
            ...field
        }) => ({
            ...field,
            ...(options ? { options: [...options] } : {}),
        })),
        renderer: {
            component: SITE_NAVIGATION_BLOCK_TYPE,
            publicComponent: SITE_NAVIGATION_BLOCK_TYPE,
        },
        responsive: { stackOnMobile: false },
        styleCapabilities: ["layout", "gap", "responsive", "spacing", "surface", "border", "radius"],
        systemOnly: true,
    }),
    blockDefinition({
        type: "accordion",
        category: "layout",
        insertable: true,
        textFlow: false,
        standalone: false,
        layoutContainer: true,
        dropLines: layoutDropLines,
        zones: [{ key: "content", kind: "textFlow", textFlow: true, extendedRows: true, dropLines: allDropLines }],
        contextualDefaults: {
            options: {
                accordionIconMode: "arrow",
                accordionStyle: "boxed",
                accordionTransition: "slide",
            },
        },
        inspector: [
            ...layoutInspector.filter(({ key }) => key !== "stackOnMobile" && key !== "alignment"),
            { key: "accordionStyle", kind: "select", options: ["boxed", "highlightActive"], scope: "options" },
            { key: "accordionIconMode", kind: "select", options: ["arrow", "plusMinus", "custom"], scope: "options" },
            { key: "accordionClosedIcon", kind: "text", scope: "options" },
            { key: "accordionOpenIcon", kind: "text", scope: "options" },
            { key: "accordionTransition", kind: "select", options: ["none", "slide", "fade"], scope: "options" },
        ],
        styleCapabilities: ["layout", "gap", "spacing", "surface", "border", "radius"],
    }),
    blockDefinition({
        type: "accordionItem",
        category: "layout",
        insertable: true,
        textFlow: false,
        standalone: true,
        layoutContainer: false,
        dropLines: blockDropLines,
        contextualDefaults: { content: { title: "", text: "" } },
        inspector: [
            { key: "title", kind: "text", scope: "content" },
            { key: "text", kind: "richText", scope: "content" },
        ],
        styleCapabilities: ["surface", "border", "radius", "spacing"],
        warnings: [missingContentWarning],
    }),
    blockDefinition({
        type: "columns",
        category: "layout",
        insertable: true,
        textFlow: false,
        standalone: false,
        layoutContainer: true,
        dropLines: layoutDropLines,
        zones: [
            { key: "column:0", kind: "textFlow", textFlow: true, dropLines: allDropLines },
            { key: "column:1", kind: "textFlow", textFlow: true, dropLines: allDropLines },
        ],
        inspector: layoutInspector,
        responsive: { stackOnMobile: true },
        styleCapabilities: ["layout", "gap", "responsive", "spacing"],
    }),
    blockDefinition({
        type: "separator",
        category: "utility",
        insertable: true,
        contextualDefaults: { options: { borderWidth: "1px", separatorStyle: "solid", width: "100%" } },
        textFlow: false,
        standalone: true,
        layoutContainer: false,
        dropLines: blockDropLines,
        inspector: [
            { key: "borderColor", kind: "color", scope: "style" },
            { key: "width", kind: "select", options: ["100%", "75%", "66%", "50%", "33%", "25%"], scope: "style" },
            { key: "separatorStyle", kind: "select", options: ["solid", "dashed", "dotted", "double"], scope: "style" },
            { key: "borderWidth", kind: "number", scope: "style" },
        ],
        styleCapabilities: ["color", "spacing", "size", "border"],
    }),
];

const extraInsertableTypes: Array<[string, SiteBuilderBlockCategory, SiteBuilderDropLine[], Partial<SiteBuilderBlockDefinition>]> = [
    ["video", "media", blockDropLines, {
        contextualDefaults: {
            content: { title: "Video", videoUrl: "" },
            options: { aspectRatio: "wide" },
        },
        inspector: [
            { key: "videoUrl", kind: "url", scope: "content" },
            { key: "title", kind: "text", scope: "content" },
            { key: "aspectRatio", kind: "select", options: ["wide", "standard", "square"], scope: "options" },
        ],
        warnings: [missingMediaWarning],
    }],
    ["iconList", "utility", blockDropLines, {
        contextualDefaults: {
            content: { icon: "lucide:check", items: "First item\nSecond item" },
        },
        inspector: [
            { key: "icon", kind: "text", scope: "content" },
            { key: "items", kind: "richText", scope: "content" },
        ],
        warnings: [missingContentWarning],
    }],
    ["countdown", "data", blockDropLines, {
        contextualDefaults: {
            content: { targetDate: "", title: "" },
            options: { gridH: 2, gridW: 3 },
        },
        inspector: [
            { key: "title", kind: "text", scope: "content" },
            { key: "targetDate", kind: "dateTime", scope: "content" },
        ],
        warnings: [{ code: "incompleteValue", severity: "info", field: "targetDate" }],
    }],
    ["donationButton", "commerce", inlineControlDropLines, {
        contextualDefaults: {
            content: { href: "#", icon: "lucide:heart", label: "Podpořit projekt" },
            options: { alignment: "center", borderRadius: "9999px", buttonPreset: "primary", size: "medium" },
        },
        inspector: [
            { key: "label", kind: "text", scope: "content" },
            { key: "href", kind: "url", scope: "content" },
            { key: "icon", kind: "text", scope: "content" },
            ...buttonInspector.slice(2),
        ],
        warnings: [missingContentWarning, missingLinkWarning],
    }],
    ["addToCartButton", "commerce", inlineControlDropLines, {
        contextualDefaults: {
            content: { icon: "lucide:bag", label: "", productUuid: "" },
            options: { alignment: "center", borderRadius: "9999px", buttonPreset: "primary", size: "medium" },
        },
        inspector: [
            { key: "label", kind: "text", scope: "content" },
            { key: "productUuid", kind: "text", scope: "content" },
            { key: "icon", kind: "text", scope: "content" },
            ...buttonInspector.slice(2),
        ],
        warnings: [missingContentWarning],
    }],
    ["supportedPaymentButton", "commerce", blockDropLines, {
        contextualDefaults: {
            content: { items: "Card payment\nBank transfer", title: "Supported payments" },
            options: { paymentLayout: "cards" },
        },
        inspector: [
            { key: "title", kind: "text", scope: "content" },
            { key: "items", kind: "richText", scope: "content" },
            { key: "paymentLayout", kind: "select", options: ["cards", "compact"], scope: "options" },
        ],
        warnings: [missingContentWarning],
    }],
    ["map", "utility", blockDropLines, {
        contextualDefaults: {
            content: { address: "", latitude: "", longitude: "", mapUrl: "", title: "" },
            options: { mapAspectRatio: "wide" },
        },
        inspector: [
            { key: "title", kind: "text", scope: "content" },
            { key: "address", kind: "richText", scope: "content" },
            { key: "mapUrl", kind: "url", scope: "content" },
            { key: "latitude", kind: "text", scope: "content" },
            { key: "longitude", kind: "text", scope: "content" },
            { key: "mapAspectRatio", kind: "select", options: ["wide", "standard", "square"], scope: "options" },
        ],
    }],
    ["avatars", "social", blockDropLines, {
        contextualDefaults: {
            content: { items: "Anna\nTomáš\nMia\nDavid", title: "" },
            options: { avatarLayout: "stack", showAvatarNames: "false" },
        },
        inspector: [
            { key: "title", kind: "text", scope: "content" },
            { key: "items", kind: "richText", scope: "content" },
            { key: "avatarLayout", kind: "select", options: ["stack", "pills"], scope: "options" },
            { key: "showAvatarNames", kind: "toggle", scope: "options" },
        ],
        warnings: [missingContentWarning],
    }],
    ["table", "layout", blockDropLines, {
        contextualDefaults: { content: { items: "" } },
        inspector: [{ key: "items", kind: "richText", scope: "content" }],
        responsive: { mobile: { overflowX: "auto" } },
    }],
    ["uploadFile", "media", blockDropLines, { inspector: [{ key: "file", kind: "media", scope: "content" }], warnings: [missingMediaWarning] }],
    ["icon", "utility", inlineControlDropLines, {
        contextualDefaults: { content: { icon: "check" } },
        inspector: [
            { key: "icon", kind: "text", scope: "content" },
            { key: "href", kind: "text", scope: "content" },
            { key: "label", kind: "text", scope: "content" },
            { key: "size", kind: "select", options: ["xs", "sm", "md", "lg", "xl", "1x", "2x", "3x", "4x", "5x"], scope: "options" },
        ],
    }],
    ["emoji", "utility", inlineControlDropLines, { contextualDefaults: { content: { text: "🙂" } }, inspector: [{ key: "text", kind: "text", scope: "content" }] }],
    ["signature", "utility", blockDropLines, { inspector: textInspector, warnings: [missingContentWarning] }],
    ["alert", "utility", blockDropLines, {
        contextualDefaults: { content: { text: "Important information" } },
        inspector: textInspector,
        warnings: [missingContentWarning],
    }],
    ["rating", "data", blockDropLines, {
        contextualDefaults: {
            content: { ratingMax: "5", ratingValue: "4.8" },
            options: { ratingIcon: "stars", ratingSize: "md", ratingTitlePosition: "left" },
        },
        inspector: [
            { key: "ratingValue", kind: "number", scope: "content" },
            { key: "ratingMax", kind: "number", scope: "content" },
            { key: "ratingIcon", kind: "select", options: ["stars", "thumbs", "circles", "squares", "hearts", "custom"], scope: "style" },
            { key: "ratingActiveIcon", kind: "text", scope: "content" },
            { key: "ratingInactiveIcon", kind: "text", scope: "content" },
            { key: "ratingIconColor", kind: "color", scope: "style" },
            { key: "ratingSize", kind: "select", options: ["xs", "sm", "md", "lg", "xl"], scope: "style" },
            { key: "ratingTitlePosition", kind: "select", options: ["top", "left", "none"], scope: "style" },
        ],
    }],
    ["socialMedia", "social", layoutDropLines, {
        contextualDefaults: {
            options: {
                gridH: 1,
                gridW: 6,
                socialAlignment: "left",
                socialIconBrandColor: "true",
                socialIconLayout: "disk",
                socialIconSize: "40",
                socialIconTheme: "default",
                socialLinkStyle: "noUnderline",
                socialTitlePosition: "none",
            },
        },
        inspector: [
            { key: "title", kind: "text", scope: "content" },
            { key: "socialAlignment", kind: "select", options: ["left", "center", "right"], scope: "options" },
            { key: "socialTitlePosition", kind: "select", options: ["none", "top", "left"], scope: "options" },
            { key: "socialIconLayout", kind: "select", options: ["square", "circle", "disk", "none"], scope: "options" },
            { key: "socialIconSize", kind: "number", scope: "options" },
            { key: "socialIconBrandColor", kind: "toggle", scope: "options" },
            { key: "socialIconTheme", kind: "select", options: ["default", "dark", "light", "primary", "muted"], scope: "options" },
            { key: "socialLinkStyle", kind: "select", options: ["underlineAlways", "underlineOnHover", "noUnderline"], scope: "options" },
            { key: "socialIconColor", kind: "color", scope: "options" },
            { key: "socialIconBackgroundColor", kind: "color", scope: "options" },
        ],
        layoutContainer: true,
        standalone: false,
        zones: [{
            key: "items",
            kind: "textFlow",
            accepts: ["socialLink"],
            capacity: 12,
            textFlow: true,
            dropLines: allDropLines,
        }],
    }],
    ["socialLink", "social", inlineControlDropLines, {
        contextualDefaults: {
            content: { href: "https://", icon: "social:link", label: "Social" },
            options: { openInNewWindow: "true" },
        },
        inspector: [
            ...socialLinkInspector,
            { key: "openInNewWindow", kind: "toggle", scope: "options" },
        ],
    }],
    ["share", "social", layoutDropLines, {
        contextualDefaults: {
            options: {
                gridH: 1,
                gridW: 6,
                shareAlignment: "left",
                shareColored: "true",
                shareIconLayout: "disk",
                shareIconSize: "40",
                shareLinkStyle: "noUnderline",
                shareTitlePosition: "none",
            },
        },
        inspector: [
            { key: "title", kind: "text", scope: "content" },
            { key: "shareAlignment", kind: "select", options: ["left", "center", "right"], scope: "options" },
            { key: "shareTitlePosition", kind: "select", options: ["none", "top", "left"], scope: "options" },
            { key: "shareIconLayout", kind: "select", options: ["square", "circle", "disk", "none"], scope: "options" },
            { key: "shareIconSize", kind: "number", scope: "options" },
            { key: "shareColored", kind: "toggle", scope: "options" },
            { key: "shareLinkStyle", kind: "select", options: ["underlineAlways", "underlineOnHover", "noUnderline"], scope: "options" },
            { key: "backgroundColor", kind: "color", scope: "style" },
        ],
        layoutContainer: true,
        standalone: false,
        warnings: [],
        zones: [{ key: "items", kind: "slot", accepts: ["icon"], capacity: 6, dropLines: allDropLines }],
    }],
    ["textHighlight", "text", blockDropLines, {
        contextualDefaults: {
            content: {
                text: "Put the focus on what you have to say!",
                title: "Text Highlight",
            },
            options: {
                alignment: "center",
                backgroundColor: "var(--site-primary-soft)",
                gridH: 2,
                gridW: 6,
                textHighlightWidth: "100%",
            },
        },
        inspector: textHighlightInspector,
        textFlow: false,
        warnings: [missingContentWarning],
        zones: [
            { key: "beforeTitle", kind: "textFlow", textFlow: true, dropLines: allDropLines },
            { key: "afterTitle", kind: "textFlow", textFlow: true, dropLines: allDropLines },
            { key: "content", kind: "textFlow", textFlow: true, dropLines: allDropLines },
        ],
    }],
    ["chart", "data", blockDropLines, {
        contextualDefaults: {
            content: {
                chartRows: "[{\"label\":\"First\",\"values\":[12]},{\"label\":\"Second\",\"values\":[24]},{\"label\":\"Third\",\"values\":[18]},{\"label\":\"Fourth\",\"values\":[17]},{\"label\":\"Fifth\",\"values\":[10]}]",
                chartSeries: "[{\"label\":\"One\",\"color\":\"var(--site-primary)\",\"borderColor\":\"var(--site-border-strong)\"}]",
                title: "A Chart Title",
            },
            options: {
                chartBorderWidth: "2",
                chartLegend: "top",
                chartTooltip: "true",
                chartType: "barVertical",
                gridH: 7,
                gridW: 6,
            },
        },
        inspector: [
            { key: "title", kind: "text", scope: "content" },
            { key: "chartType", kind: "select", options: ["barVertical", "barHorizontal", "line", "pie", "doughnut", "radar"], scope: "options" },
            { key: "chartLegend", kind: "select", options: ["none", "top", "bottom", "left", "right"], scope: "options" },
            { key: "chartTooltip", kind: "toggle", scope: "options" },
            { key: "chartBorderWidth", kind: "number", scope: "options" },
            { key: "chartMinAxis", kind: "number", scope: "options" },
            { key: "chartMaxAxis", kind: "number", scope: "options" },
        ],
        warnings: [missingContentWarning],
        zones: [
            { key: "afterTitle", kind: "textFlow", textFlow: true, dropLines: allDropLines },
        ],
    }],
    ["progressBar", "data", blockDropLines, {
        contextualDefaults: {
            content: {
                progressValue: "72",
                title: "Vyprodáno z 72 %",
            },
            options: {
                gridH: 1,
                gridW: 4,
                progressFillColor: "var(--site-primary)",
                progressLabelColor: "var(--site-on-primary)",
                progressLabelPosition: "inside",
                progressStriped: "false",
                progressTrackColor: "var(--site-border)",
            },
        },
        inspector: [
            { key: "title", kind: "text", scope: "content" },
            { key: "progressValue", kind: "number", scope: "content" },
            { key: "progressLabelPosition", kind: "select", options: ["inside", "above", "below", "after", "hide"], scope: "options" },
            { key: "progressFillColor", kind: "color", scope: "options" },
            { key: "progressTrackColor", kind: "color", scope: "options" },
            { key: "progressLabelColor", kind: "color", scope: "options" },
            { key: "progressStriped", kind: "toggle", scope: "options" },
        ],
        warnings: [{ code: "incompleteValue", severity: "info", field: "progressValue" }],
    }],
    ["badge", "utility", blockDropLines, {
        contextualDefaults: {
            content: { label: "Category" },
            options: {
                badgeStyle: "secondary",
                borderColor: "var(--site-border)",
                borderRadius: "var(--site-radius)",
                borderWidth: "1px",
                gap: "0.45rem",
                gridH: 1,
                gridW: 2,
                padding: "0.35rem 0.7rem",
            },
            slots: {
                icon: [{
                    type: "icon",
                    content: {
                        icon: "lucide:check",
                        label: "",
                    },
                    options: {
                        alignment: "none",
                        backgroundColor: "transparent",
                        borderWidth: "0px",
                        iconStyle: "plain",
                        padding: "0px",
                        size: "1x",
                    },
                }],
            },
        },
        inspector: [
            { key: "badgeStyle", kind: "select", options: badgeStyleOptions, scope: "options" },
            { key: "animation", kind: "select", options: animationOptions, scope: "options" },
        ],
        layoutContainer: true,
        zones: [{ key: "icon", kind: "slot", accepts: ["icon"], capacity: 1, dropLines: allDropLines }],
        warnings: [missingContentWarning],
    }],
    ["ctaBadge", "utility", blockDropLines, {
        contextualDefaults: {
            content: { href: "#", label: "Explore", title: "What's new ?" },
            options: {
                buttonPreset: "primary",
                gridH: 1,
                gridW: 6,
            },
        },
        inspector: [
            { key: "backgroundColor", kind: "color", scope: "options" },
            { key: "borderWidth", kind: "number", scope: "options" },
            { key: "borderStyle", kind: "select", options: borderStyleOptions, scope: "options" },
            { key: "borderColor", kind: "color", scope: "options" },
            { key: "borderRadius", kind: "number", scope: "options" },
            { key: "animation", kind: "select", options: animationOptions, scope: "options" },
        ],
        layoutContainer: true,
        zones: [{ key: "icon", kind: "slot", accepts: ["icon"], capacity: 2, dropLines: allDropLines }],
        warnings: [missingContentWarning],
    }],
    ["field", "utility", blockDropLines, {
        contextualDefaults: {
            content: { defaultValue: "", description: "", label: "Your Name", placeholder: "" },
            options: {
                choiceLayout: "vertical",
                fieldInputType: "text",
                gridH: 1,
                gridW: 6,
                requirement: "none",
                rows: "3",
                showDescription: "false",
            },
        },
        inspector: fieldInspector,
        warnings: [missingContentWarning],
    }],
    ["form", "utility", layoutDropLines, {
        contextualDefaults: {
            content: {
                actionUrl: "",
                formName: "Contact form",
                successUrl: "/contactus-thank-you",
            },
            options: {
                formAction: "sendEmail",
                formLabelsPosition: "sideGap",
                formLabelsWidth: "200px",
                formMarkedFields: "required",
                formMarker: "*",
                formOnSuccess: "redirect",
                gridH: 7,
                gridW: 6,
            },
            slots: {
                items: [{
                    type: "field",
                    content: {
                        defaultValue: "",
                        label: "Your Name",
                        name: "name",
                        placeholder: "",
                    },
                    options: {
                        choiceLayout: "vertical",
                        fieldInputType: "text",
                        gridH: 1,
                        gridW: 6,
                        requirement: "required",
                        rows: "3",
                        showDescription: "false",
                    },
                }, {
                    type: "field",
                    content: {
                        defaultValue: "",
                        label: "Your Email",
                        name: "email",
                        placeholder: "",
                    },
                    options: {
                        choiceLayout: "vertical",
                        fieldInputType: "email",
                        gridH: 1,
                        gridW: 6,
                        requirement: "required",
                        rows: "3",
                        showDescription: "false",
                    },
                }, {
                    type: "field",
                    content: {
                        defaultValue: "",
                        label: "Message",
                        name: "message",
                        placeholder: "",
                    },
                    options: {
                        choiceLayout: "vertical",
                        fieldInputType: "longText",
                        gridH: 2,
                        gridW: 6,
                        requirement: "required",
                        rows: "4",
                        showDescription: "false",
                    },
                }, {
                    type: "submitButton",
                    content: { label: "Submit" },
                    options: {
                        buttonPosition: "right",
                        gridH: 1,
                        gridW: 6,
                    },
                }],
            },
        },
        inspector: formInspector,
        layoutContainer: true,
        standalone: false,
        textFlow: false,
        warnings: [],
        zones: [
            { key: "items", kind: "slot", accepts: ["field", "heading", "paragraph", "separator", "submitButton"], capacity: 64, dropLines: allDropLines },
        ],
    }],
    ["submitButton", "utility", blockDropLines, {
        contextualDefaults: {
            content: { label: "Submit" },
            options: { buttonPosition: "right", gridH: 1, gridW: 6 },
        },
        inspector: submitButtonInspector,
        warnings: [missingContentWarning],
    }],
    ["blockquote", "text", blockDropLines, {
        contextualDefaults: {
            content: {
                author: "",
                text: "",
            },
            options: {
                authorAlignment: "left",
                backgroundColor: "var(--site-surface)",
                blockquoteDecoration: "none",
                blockquoteShape: "none",
                blockquoteWidth: "100%",
                borderRadius: "var(--site-radius)",
                borderWidth: "0px",
                edgeSpacing: "20px",
                gridH: 3,
                gridW: 5,
                showAuthor: "true",
            },
        },
        inspector: blockquoteInspector,
        layoutContainer: true,
        textFlow: false,
        warnings: [missingContentWarning],
        zones: [
            { key: "icon", kind: "slot", accepts: ["icon"], capacity: 1, dropLines: allDropLines },
            { key: "beforeText", kind: "textFlow", textFlow: true, dropLines: allDropLines },
            { key: "afterText", kind: "textFlow", textFlow: true, dropLines: allDropLines },
            { key: "beforeImage", kind: "textFlow", textFlow: true, dropLines: allDropLines },
            { key: "media", kind: "slot", accepts: ["image"], capacity: 1, dropLines: allDropLines },
            { key: "afterImage", kind: "textFlow", textFlow: true, dropLines: allDropLines },
        ],
    }],
    ["searchBar", "utility", blockDropLines, {
        contextualDefaults: {
            content: { buttonLabel: "", placeholder: "" },
            options: {
                gridH: 1,
                gridW: 6,
                searchResultLimit: "6",
                searchWithin: "everything",
            },
        },
        inspector: [
            { key: "searchWithin", kind: "select", options: ["everything", "pages"], scope: "options" },
            { key: "searchResultLimit", kind: "number", scope: "options" },
        ],
        layoutContainer: true,
        zones: [
            { key: "leadingIcon", kind: "slot", accepts: ["icon"], capacity: 1, dropLines: allDropLines },
            { key: "actionIcon", kind: "slot", accepts: ["icon"], capacity: 1, dropLines: allDropLines },
        ],
    }],
];

const blockDefinitions = new Map<string, SiteBuilderBlockDefinition>();

for (const definition of coreBlockDefinitions) {
    blockDefinitions.set(definition.type, definition);
}

for (const [type, category, dropLines, extra] of extraInsertableTypes) {
    blockDefinitions.set(type, blockDefinition({
        type,
        category,
        insertable: true,
        textFlow: category === "text",
        standalone: true,
        layoutContainer: false,
        dropLines,
        ...extra,
        styleCapabilities: extra.styleCapabilities ?? [
            "typography",
            "color",
            "surface",
            "border",
            "radius",
            "spacing",
            "size",
        ],
    }));
}

export function getSiteBuilderBlockDefinition(type: string): SiteBuilderBlockDefinition {
    return blockDefinitions.get(type) ?? blockDefinition({
        type,
        category: "utility",
        insertable: true,
        textFlow: false,
        standalone: true,
        layoutContainer: false,
        dropLines: blockDropLines,
        styleCapabilities: ["color", "spacing", "size"],
    });
}

export function siteBuilderDropLinesForBlock(block: SiteTemplateBlock | string): SiteBuilderDropLine[] {
    return getSiteBuilderBlockDefinition(typeof block === "string" ? block : block.type).dropLines;
}

export function siteBuilderDropLinesForPlacement(
    anchorBlock: SiteTemplateBlock | string,
    incomingBlock: SiteTemplateBlock | string | null | undefined,
): SiteBuilderDropLine[] {
    const anchorType = typeof anchorBlock === "string" ? anchorBlock : anchorBlock.type;
    const incomingType = incomingBlock
        ? typeof incomingBlock === "string" ? incomingBlock : incomingBlock.type
        : null;

    if (textFlowDropLineTypes.has(anchorType)) {
        return textDropLines;
    }

    if (anchorType === "badge" || anchorType === "blockquote" || anchorType === "ctaBadge" || anchorType === "form" || anchorType === "searchBar" || anchorType === "share" || anchorType === "socialMedia" || anchorType === "textHighlight") {
        return ["before", "below"];
    }

    if (anchorType === "icon") {
        return incomingType === "icon" ? ["right", "below"] : textDropLines;
    }

    if (actionDropLineTypes.has(anchorType)) {
        return incomingType && actionDropLineTypes.has(incomingType)
            ? ["right", "below"]
            : textDropLines;
    }

    return siteBuilderDropLinesForBlock(anchorBlock);
}

export function siteBuilderBlockAllowsDropLine(block: SiteTemplateBlock | string, line: SiteBuilderDropLine): boolean {
    return siteBuilderDropLinesForBlock(block).includes(line);
}

export function siteBuilderPlacementAllowsDropLine(
    anchorBlock: SiteTemplateBlock | string,
    incomingBlock: SiteTemplateBlock | string | null | undefined,
    line: SiteBuilderDropLine,
): boolean {
    return siteBuilderDropLinesForPlacement(anchorBlock, incomingBlock).includes(line);
}

export function siteBuilderBlockCreatesLayoutOnDrop(block: SiteTemplateBlock | string, line: SiteBuilderDropLine): boolean {
    const definition = getSiteBuilderBlockDefinition(typeof block === "string" ? block : block.type);

    return line === "left" || line === "right" || line === "below" || definition.layoutContainer;
}

export function siteBuilderBlockIsTextFlow(block: SiteTemplateBlock | string): boolean {
    return getSiteBuilderBlockDefinition(typeof block === "string" ? block : block.type).textFlow;
}

export function siteBuilderBlockIsLayoutContainer(block: SiteTemplateBlock | string): boolean {
    return getSiteBuilderBlockDefinition(typeof block === "string" ? block : block.type).layoutContainer;
}

export function siteBuilderStyleSnapshotFromContext(
    design: SiteTemplateDesign | null | undefined,
    context?: SiteBuilderStyleSnapshot["context"],
): SiteBuilderStyleSnapshot {
    return {
        colors: design?.colors ? { ...design.colors } : undefined,
        typography: design?.typography ? { ...design.typography } : undefined,
        layout: design?.layout ? { ...design.layout } : undefined,
        components: design?.components ? { ...design.components } : undefined,
        context,
    };
}

export function withSiteBuilderStyleSnapshot(
    block: SiteTemplateBlock,
    snapshot: SiteBuilderStyleSnapshot,
): SiteTemplateBlock {
    return {
        ...block,
        options: {
            ...(block.options ?? {}),
            [SITE_BUILDER_STYLE_SNAPSHOT_OPTION]: snapshot,
        },
    };
}

export function withSiteBuilderLayoutContainer(block: SiteTemplateBlock): SiteTemplateBlock {
    return {
        ...block,
        options: {
            ...(block.options ?? {}),
            [SITE_BUILDER_LAYOUT_CONTAINER_OPTION]: true,
        },
    };
}

function stringValue(value: unknown): string {
    return typeof value === "string" ? value.trim() : "";
}

function firstStringValue(block: SiteTemplateBlock, keys: string[]): string {
    for (const key of keys) {
        const contentValue = stringValue(block.content?.[key]);
        if (contentValue !== "") {
            return contentValue;
        }

        const optionValue = stringValue(block.options?.[key]);
        if (optionValue !== "") {
            return optionValue;
        }
    }

    return "";
}

function hasMeaningfulLink(block: SiteTemplateBlock): boolean {
    const href = firstStringValue(block, ["href", "url", "link"]);

    return href !== "" && href !== "#";
}

function hasMeaningfulMedia(block: SiteTemplateBlock): boolean {
    return firstStringValue(block, ["image", "imageUrl", "src", "file", "fileUrl", "mediaUrl", "videoUrl"]) !== "";
}

function hasMeaningfulContent(block: SiteTemplateBlock): boolean {
    return firstStringValue(block, ["title", "text", "label", "code", "items", "description", "value"]) !== "";
}

export function siteBuilderWarningsForBlock(block: SiteTemplateBlock): SiteBuilderResolvedWarning[] {
    const definition = getSiteBuilderBlockDefinition(block.type);

    return definition.warnings
        .filter((warning) => {
            if (warning.code === "missingContent") {
                return !hasMeaningfulContent(block);
            }

            if (warning.code === "missingLink") {
                return !hasMeaningfulLink(block);
            }

            if (warning.code === "missingMedia") {
                return !hasMeaningfulMedia(block);
            }

            return false;
        })
        .map((warning) => ({
            ...warning,
            blockType: block.type,
        }));
}
